/**
 * @file   src\form\BuyerListingCreateWantAdMob.js
 * @brief  This file is responsible for buyerlisting create want ad in mobile view
 * @date   May, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import WantAdSummary from "../../mobile/buyerlistingServiceCreateAd/v2/WantAdSummary";
import AddLocation from "../../mobile/components/v2/AddLocation";
import AddPropertyType from "../../mobile/components/v2/AddPropertyType";
import { BUY, WANT_AD_KEY, WANT_AD_KEY_TEMP } from "../../constants/common";
import { useNavigate, useParams } from "react-router-dom";
import { getLocalStorage, setLocalStorage } from "../../helpers/common";
import AddTimeLine from "../../mobile/components/v2/AddTimeline";
import AddMustHaves from "../../mobile/components/createWantAd/v2/AddMustHaves";
import NiceToHaves from "../../mobile/components/createWantAd/v2/NiceToHaves";
import AddTitle from "../../mobile/components/createWantAd/AddTitle";
import AddPurchaseType from "../../mobile/components/createWantAd/v2/AddPurchaseType";
import AddFinancing from "../../mobile/components/createWantAd/v2/AddFinancing";
import AddBudget from "../../mobile/components/createWantAd/v2/AddBudget";
import { saveToDraftBuyerListing } from "../../helpers/createWantAd";
import ErrorMessage from "../../mobile/commonComponents/ErrorMessage";
import { pageURLs } from "../../constants/pageURLs";
import { getAgentDetails } from "../../services/v2/agentClientsService";
import { saveBuyerListStrengthenWantAd } from "../../services/v2/wantAdService";
import AddBuyerInfo from "../../mobile/components/v2/AddBuyerInfo";
import {
  BUDGET_KEYS,
  LOCATION_KEYS,
  PROPERTY_TYPE_KEYS,
  STEP_WEIGHTS,
} from "../../constants/v2/buyerListingForm";

const BuyerListingCreateWantAdFormMob = (props) => {
  const [uiKey, setUIKey] = useState(null);
  const [formValues, setFormValues] = useState(
    getLocalStorage(WANT_AD_KEY) || {},
  );
  const [completedPercentage, setCompletedPercentage] = useState(
    formValues.cmpldPerctg || 0,
  );
  const [completedSteps, setCompletedSteps] = useState(() => {
    const savedSteps = getLocalStorage(WANT_AD_KEY)?.completedSteps || [];
    return new Set(savedSteps);
  });
  let adAmenities = null;
  const isAgent = false;
  const isEdit = false;
  const adType = _.get(formValues, "adType", "BUY");
  const isFromBuyerListingService = true;
  const { id:slug } = useParams();
  const [isValidId, setIsValidId] = useState(null);
  const navigate = useNavigate();
  const [showUrlExpired, setShowUrlExpired] = useState(false);
  const [agentDetails, setAgentDetails] = useState(null);

  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    getWantAdDetails();
  }, [slug]);

  const getWantAdDetails = async () => {
    try {
      // const splitId = slug.split("-");
      // const agentID = splitId[splitId.length - 1];

      if (!slug) {
        setIsValidId(0);
        return;
      }

      const response = await getAgentDetails(slug);
      const responseResult = _.get(response, "result", null);

      // If no result data, treat as an error
      if (!responseResult?.data) {
        setIsValidId(0);
        return;
      }

      setAgentDetails(responseResult.data);

      if (responseResult.isReadyToReview) {
        if (responseResult.isPublished) {
          setShowUrlExpired(true);
        } else {
          navigate(pageURLs?.buyerListingConfirmation, {
            state: { agentDetails: responseResult.data },
          });
        }
      } else {
        setIsValidId(1);
      }
    } catch (err) {
      console.log("Error", err);
      setIsValidId(0);
    }
  };

  useEffect(() => {
    handleCreateWantAdUi();
    if (
      uiKey === "clearBudget" ||
      uiKey === "clearLocation" ||
      uiKey === "clearPropertyType" ||
      uiKey === "clearNiceToHaves"
    ) {
      clearPreviousStates({ type: uiKey });
    }

    let wantAdData = getLocalStorage(WANT_AD_KEY);

    setLocalStorage(WANT_AD_KEY, { ...wantAdData, backArrowClicked: false });
  }, [uiKey]);

  // to save value to draft
  const handleSaveToDraft = () => {
    let wantAdData = getLocalStorage(WANT_AD_KEY);
    if (wantAdData?.isPublished != 1) {
      const type = "draft";
      saveWantAdData(type);
    }
  };

  const updateCompletionPercentage = (stepKey, type) => {
    //When property type is changed
    if (type === "change") {
      const stepsToRemove = ["mustHaves", "niceToHaves", "budget"];
      const newCompletedSteps = new Set(completedSteps);

      stepsToRemove.forEach((step) => {
        newCompletedSteps.delete(step);
      });

      setCompletedSteps(newCompletedSteps);

      const newPercentage = Array.from(newCompletedSteps).reduce(
        (total, step) => total + STEP_WEIGHTS[step],
        0,
      );

      setCompletedPercentage(newPercentage);
      return;
    }

    if (!completedSteps.has(stepKey)) {
      const newCompletedSteps = new Set(completedSteps);
      newCompletedSteps.add(stepKey);
      setCompletedSteps(newCompletedSteps);

      const newPercentage = Array.from(newCompletedSteps).reduce(
        (total, step) => total + STEP_WEIGHTS[step],
        0,
      );

      setCompletedPercentage(newPercentage);
    }
    setIsSaved((prev) => !prev);
  };

  const removeCompletionStep = (stepToRemove) => {
    const newCompletedSteps = new Set(completedSteps);

    if (typeof stepToRemove === "string") {
      newCompletedSteps.delete(stepToRemove);
    } else if (Array.isArray(stepToRemove)) {
      stepToRemove.forEach((step) => newCompletedSteps.delete(step));
    }

    setCompletedSteps(newCompletedSteps);

    const newPercentage = Array.from(newCompletedSteps).reduce(
      (total, step) => total + STEP_WEIGHTS[step],
      0,
    );

    setCompletedPercentage(newPercentage);
  };

  const saveStrengthenData = async (wantAdIdValue, type) => {
    const wantAd = getLocalStorage(WANT_AD_KEY);
    const wantAdId = wantAdIdValue;

    if (wantAd) {
      let requestPayLoad = {};
      let isApproveLoan = "";

      if (
        _.get(wantAd, "isPreApprLoan", null) === "Yes" ||
        _.get(wantAd, "isPreApprLoan", null) == true
      ) {
        isApproveLoan = true;
      } else if (
        _.get(wantAd, "isPreApprLoan", null) === "No" ||
        _.get(wantAd, "isPreApprLoan", null) == true
      ) {
        isApproveLoan = false;
      }
      requestPayLoad.isPreApprLoan === isApproveLoan;
      const is1031Exchange = wantAd.is1031Exchange === "Yes" ? true : false;
      requestPayLoad.wantAdId = wantAdId;
      requestPayLoad.isPreApprLoan = isApproveLoan;
      requestPayLoad.is1031Exchange = is1031Exchange;
      requestPayLoad.completedStep = 1;
      requestPayLoad.adType = "BUY_STRENGTHEN";
      requestPayLoad.maxBdgt = wantAd.maxBdgt;
      requestPayLoad.minBdgt = wantAd.minBdgt;
      requestPayLoad.pFinance = wantAd.pFinance || wantAd.finTypeId;
      requestPayLoad.purchaseType = wantAd.purchaseType || wantAd.prchTypeId;

      await saveBuyerListStrengthenWantAd(requestPayLoad)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (type == "save") {
              localStorage.removeItem(WANT_AD_KEY);
              localStorage.removeItem(WANT_AD_KEY_TEMP);
              navigate(pageURLs?.buyerListingConfirmation, {
                state: { agentDetails: agentDetails },
              });
            }
          }
        })
        .catch((err) => {
          console.log("Error", err);
          setIsValidId(0);
        });
    } else {
      handleStateClear();
    }
  };
  /**
   * To save want ad data
   * @param {*} type
   */
  const saveWantAdData = async (type) => {
    const requestPayLoad = getLocalStorage(WANT_AD_KEY);
    requestPayLoad.agentId = agentDetails.UserId;
    requestPayLoad.cmpldPerctg = 100;
    requestPayLoad.adType = "BUY";
    requestPayLoad.clientFirstName = formValues.clientFirstName;
    requestPayLoad.clientLastName = formValues.clientLastName;
    requestPayLoad.clientEmail = formValues.clientEmail;
    requestPayLoad.clientPhone = formValues.clientPhone;
    let arrayValue = [];
    if (requestPayLoad?.zipIdValues) {
      requestPayLoad?.zipIdValues.map((zip) => {
        arrayValue.push(zip?.zipId);
      });
      requestPayLoad.zipId = arrayValue;
    } else if (requestPayLoad?.zipCode) {
      requestPayLoad?.zipCode.map((zip) => {
        if (zip?.zipId && zip?.zipId != null) {
          arrayValue.push(zip?.zipId);
        }
      });
      requestPayLoad.zipId = arrayValue;
    }
    requestPayLoad.isZipcodeBased =
      requestPayLoad.zipId?.length > 0 ? true : false;
    if (type === "save") {
      requestPayLoad.isReadyToReview = true;
    }
    if (requestPayLoad) {
      await saveToDraftBuyerListing({ requestPayLoad: requestPayLoad }).then(
        (response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (
              requestPayLoad.purchaseType ||
              requestPayLoad.prchTypeId ||
              requestPayLoad.pFinance
            ) {
              saveStrengthenData(responseResult.wantAdId, type);
            } else {
              if (type === "save") {
                localStorage.removeItem(WANT_AD_KEY);
                localStorage.removeItem(WANT_AD_KEY_TEMP);
                navigate(pageURLs?.buyerListingConfirmation, {
                  state: { agentDetails: agentDetails },
                });
              }
            }
          } else {
            setIsValidId(0);
            console.log("Something went wrong");
          }
        },
      );
    }
  };

  useEffect(() => {
    setLocalStorage(WANT_AD_KEY_TEMP, {
      ...formValues,
      cmpldPerctg: completedPercentage,
      completedSteps: Array.from(completedSteps), // Convert Set to Array for storage
    });

    setLocalStorage(WANT_AD_KEY, {
      ...formValues,
      cmpldPerctg: completedPercentage,
      completedSteps: Array.from(completedSteps),
    });
  }, [isSaved, completedPercentage, completedSteps]);

  const handleFormValues = (stateObject) => {
    if (stateObject) {
      Object.keys(stateObject).forEach(function (key) {
        setFormValues((prevState) => ({
          ...prevState,
          [key]: stateObject[key],
        }));
      });
    }
  };
  /**
   * To clear previous states
   * @param {*} params
   */
  const clearPreviousStates = (params) => {
    if (params) {
      const { type } = params;
      if (type === "clearAll") {
        if (isEdit && !isPremiumUser) {
          setFormValues({
            wantAdId: _.get(formValues, "wantAdId", ""),
            client_Want_ad_Id: _.get(formValues, "client_Want_ad_Id", ""),
            isFilledByClient: _.get(formValues, "isFilledByClient", ""),
            clientData: _.get(formValues, "clientData", ""),
            clientId: _.get(formValues, "clientId", ""),
            clientName: _.get(formValues, "clientName", ""),
            isAgentCreated: _.get(formValues, "isAgentCreated", ""),
          });
        } else if (isPremiumUser && isEdit) {
          setFormValues({
            clientId: _.get(formValues, "clientId", ""),
            clientName: _.get(formValues, "clientName", ""),
            clientData: _.get(formValues, "clientData", ""),
            wantAdId: _.get(formValues, "wantAdId", ""),
            userFirstName: _.get(formValues, "userFirstName", ""),
            userLastName: _.get(formValues, "userLastName", ""),
            isAgentAsSeller: _.get(formValues, "isAgentAsSeller", ""),
            client_Want_ad_Id: _.get(formValues, "client_Want_ad_Id", ""),
            isFilledByClient: _.get(formValues, "isFilledByClient", ""),
            isAgentCreated: _.get(formValues, "isAgentCreated", ""),
          });
        } else if (!isEdit && isPremiumUser) {
          setFormValues({
            clientId: _.get(formValues, "clientId", ""),
            clientName: _.get(formValues, "clientName", ""),
            clientData: _.get(formValues, "clientData", ""),
            client_Want_ad_Id: _.get(formValues, "client_Want_ad_Id", ""),
            isFilledByClient: _.get(formValues, "isFilledByClient", ""),
            isAgentCreated: _.get(formValues, "isAgentCreated", ""),
          });
        } else {
          setFormValues({});
        }
      } else if (type === "clearFlexDates") {
        const array = { ...formValues }; // make a separate copy of the state
        ["duration", "month"].forEach((e) => delete array[e]);
        setFormValues(array);
      } else if (type === "dateRange") {
        const array = { ...formValues }; // make a separate copy of the state
        ["startDate", "endDate"].forEach((e) => delete array[e]);
        setFormValues(array);
      } else if (type === "moveInTypeChangeLTR") {
        const array = { ...formValues }; // make a separate copy of the state
        ["startDate"].forEach((e) => delete array[e]);
        setFormValues(array);
      } else if (type === "clearBudget") {
        const updatedValues = handleClearAndRestore(BUDGET_KEYS, formValues);
        setFormValues(updatedValues);
      } else if (type === "clearLocation") {
        const updatedValues = handleClearAndRestore(LOCATION_KEYS, formValues);
        setFormValues(updatedValues);
      } else if (type === "clearPropertyType") {
        const updatedValues = handleClearAndRestore(
          PROPERTY_TYPE_KEYS,
          formValues,
        );
        setFormValues(updatedValues);
      } else if (type === "clearNiceToHaves") {
        const updatedValues = handleClearAndRestore(["nice2Have"], formValues);
        setFormValues(updatedValues);
      }
    } else {
      // clear previous state values if user changes property type (eg from lot-land to other types and vice versa)
      const array = { ...formValues }; // make a separate copy of the state
      PROPERTY_TYPE_KEYS.forEach((e) => delete array[e]);

      setFormValues(array);
      removeCompletionStep(["mustHaves", "niceToHaves", "budget"]);
    }
  };

  /**
   * To handle unsaved form changes when user pressed backArrow
   */
  const handleClearAndRestore = (keysToHandle, formValues) => {
    const array = { ...formValues };
    const storedData = getLocalStorage(WANT_AD_KEY);

    keysToHandle.forEach((key) => {
      delete array[key];
      if (storedData?.[key]) {
        array[key] = storedData[key];
      }
    });

    return array;
  };

  /**
   * To handle create want ad Ui
   */
  const handleCreateWantAdUi = () => {
    switch (uiKey) {
      case "buyerInfo":
        return (
          <AddBuyerInfo
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("buyerInfo")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "location":
        return (
          <AddLocation
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("location")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "timeLine":
        return (
          <AddTimeLine
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("timeLine")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "budget":
        return (
          <AddBudget
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("budget")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "propertyType":
        return (
          <AddPropertyType
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("propertyType")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "mustHaves":
        return (
          <AddMustHaves
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("mustHaves")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "niceToHaves":
        return (
          <NiceToHaves
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("niceToHaves")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "title":
        return (
          <AddTitle
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("title")}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "purchaseType":
        return (
          <AddPurchaseType
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("purchaseType")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );
      case "financing":
        return (
          <AddFinancing
            handleFormValues={handleFormValues}
            formValues={formValues}
            setUIKey={setUIKey}
            adType={adType}
            completedPercentage={completedPercentage}
            clearPreviousStates={clearPreviousStates}
            setCompletedPercentage={setCompletedPercentage}
            onStepComplete={() => updateCompletionPercentage("financing")}
            isAgent={isAgent}
            isEdit={isEdit}
            isFromBuyerListingService={isFromBuyerListingService}
          />
        );

      default:
        return (
          <WantAdSummary
            setUIKey={setUIKey}
            formValues={formValues}
            agentDetails={agentDetails}
            completedPercentage={completedPercentage}
            uiKey={uiKey}
            setCompletedPercentage={setCompletedPercentage}
            isEdit={isEdit}
            saveWantAdData={saveWantAdData}
            handleFormValues={handleFormValues}
          />
        );
    }
  };

  return (
    <>
      {showUrlExpired ? (
        <ErrorMessage message="The URL cannot be accessed as the link has expired." />
      ) : isValidId === 0 ? (
        <ErrorMessage message="The url is not valid or Server not responding!Please Try again in sometime!" />
      ) : isValidId === 1 ? (
        handleCreateWantAdUi()
      ) : (
        <></>
      )}
    </>
  );
};

export default BuyerListingCreateWantAdFormMob;
